import React from 'react'
import s from './ReviewRatings.module.scss'
import { translateRatingToDecimal } from '@lib/utils/translateRatingToDecimal'
import { ReviewStars } from '../ReviewStars'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'
import Star from '@components/icons/Star'

import type { ProductReviews } from '@pylot-data/pylotschema'

type ReviewRatingsProps = {
    reviews: ProductReviews
    ratingSummary?: number
    reviewCount: number
}

const countReviewRates = (arr: string[] | number[]) => {
    const counts: any = {
        oneStar: 0,
        twoStars: 0,
        threeStars: 0,
        fourStars: 0,
        fiveStars: 0
    }

    arr.forEach((number: any) => {
        if (number > 5) return
        if (number == 0) {
            counts['oneStar']++
        } else {
            const key = [
                'oneStar',
                'twoStars',
                'threeStars',
                'fourStars',
                'fiveStars'
            ][number - 1]
            counts[key]++
        }
    })
    return counts
}

const ReviewRatings: React.FC<ReviewRatingsProps> = ({
    reviews,
    ratingSummary,
    reviewCount
}) => {
    const { items } = reviews
    // SSR-safe: isMobileScreen() rompe hydration (Star vs ReviewStars).
    const isMobile = useIsMobileScreen()

    const totalRatings = items.map((item) => {
        const averageRating = item?.average_rating

        const ratingNumber = Math.floor(
            translateRatingToDecimal(averageRating, true)
        )

        return ratingNumber
    })

    const reviewRateCount = countReviewRates(totalRatings)

    const calcPercentage = (value: number) => {
        if (value == 0) {
            return 0
        } else {
            return Math.floor((value * 100) / reviewCount)
        }
    }

    return (
        <div>
            <div className={s['ratings-layout']}>
                <div className={s['ratings-title']}>
                    {translateRatingToDecimal(ratingSummary)}{' '}
                    {isMobile ? (
                        <Star />
                    ) : (
                        <ReviewStars rating={ratingSummary} />
                    )}
                </div>
                <div className={s['ratings-container']}>
                    <div className={s['ratings-section']}>
                        <div className={s['ratings-bar']}>
                            <div
                                className={s['ratings-bar-inner']}
                                style={{
                                    width: `${calcPercentage(
                                        reviewRateCount.fiveStars
                                    )}%`
                                }}
                            />
                        </div>
                        <div className={s['ratings-value']}>5</div>
                    </div>
                    <div className={s['ratings-section']}>
                        <div className={s['ratings-bar']}>
                            <div
                                className={s['ratings-bar-inner']}
                                style={{
                                    width: `${calcPercentage(
                                        reviewRateCount.fourStars
                                    )}%`
                                }}
                            />
                        </div>
                        <div className={s['ratings-value']}>4</div>
                    </div>
                    <div className={s['ratings-section']}>
                        <div className={s['ratings-bar']}>
                            <div
                                className={s['ratings-bar-inner']}
                                style={{
                                    width: `${calcPercentage(
                                        reviewRateCount.threeStars
                                    )}%`
                                }}
                            />
                        </div>
                        <div className={s['ratings-value']}>3</div>
                    </div>
                    <div className={s['ratings-section']}>
                        <div className={s['ratings-bar']}>
                            <div
                                className={s['ratings-bar-inner']}
                                style={{
                                    width: `${calcPercentage(
                                        reviewRateCount.twoStars
                                    )}%`
                                }}
                            />
                        </div>
                        <div className={s['ratings-value']}>2</div>
                    </div>
                    <div className={s['ratings-section']}>
                        <div className={s['ratings-bar']}>
                            <div
                                className={s['ratings-bar-inner']}
                                style={{
                                    width: `${calcPercentage(
                                        reviewRateCount.oneStar
                                    )}%`
                                }}
                            />
                        </div>
                        <div className={s['ratings-value']}>1</div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ReviewRatings
